import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { InputCatalogue } from "./InputCatalogue";
import { EGInput } from "../Input/EGInput";
import { EGButton } from "../Button/EGButton/EGButton";
import { EGIconButton } from "../Button/IconButton/IconButton";
import {
  buildAction,
  getActionLabel,
  isLeafPath,
  resolveActionLabel,
} from "../../../helpers/correctiveActions";

/**
 * Selector de acciones (medidas / sanciones) con seleccion multiple.
 *
 * Es el unico selector de la app para estas listas, y por eso soporta los dos
 * modos que antes vivian separados:
 *
 *  - Con catalogo: navega los niveles como InputCatalogue y, al llegar a una
 *    hoja, agrega esa ruta a la lista. Era el modo de la conclusion de la
 *    investigacion, que ahora tambien usa el flujo de atencion.
 *  - Sin catalogo: multi-select plano sobre las opciones hardcodeadas, que es
 *    como venia funcionando Resolucion para los tenants sin catalogo.
 *
 * En ambos casos el valor sale con el mismo shape ({ path, label, locales }),
 * que es lo que permite arrastrar lo que propuso el investigador hacia las
 * sanciones sin traducir nada en el camino.
 *
 * @param value Acciones elegidas: [{ path, label, locales }]
 * @param onChange Recibe la lista completa tras cada cambio
 * @param options Opciones planas de respaldo cuando no hay catalogo
 * @param another Acciones capturadas a mano: ["texto", ...]
 * @param onAnotherChange Recibe la lista completa de textos libres
 */
export const CatalogueMultiSelect = ({
  label,
  placeholder,
  catalogue,
  isOwn = false,
  rootPath = "/",
  options = [],
  value = [],
  onChange,
  another = [],
  onAnotherChange,
  isValid,
  freeTextLabel,
  freeTextPlaceholder,
  addFreeTextTitle,
  disabled = false,
}) => {
  const [t] = useTranslation("report");
  const theme = useTheme();

  // Ruta que el usuario lleva seleccionada en el navegador de niveles.
  const [currentPath, setCurrentPath] = useState("");
  // Se usa como key del InputCatalogue para reiniciarlo tras agregar.
  const [selectorKey, setSelectorKey] = useState(0);
  const [isLeaf, setIsLeaf] = useState(false);
  const [isResolving, setIsResolving] = useState(false);

  const hasCatalogue = !!catalogue;
  const showError =
    isValid === false && value.length === 0 && another.length === 0;

  // Cambiar de catalogo (p.ej. al alternar medida/sancion) invalida lo que se
  // llevaba navegado.
  useEffect(() => {
    setCurrentPath("");
    setIsLeaf(false);
  }, [catalogue, rootPath]);

  // Solo se puede agregar cuando la ruta llego a una hoja del catalogo.
  useEffect(() => {
    let mounted = true;
    if (!currentPath || !hasCatalogue) {
      setIsLeaf(false);
      return undefined;
    }
    isLeafPath({ catalogue, path: currentPath, isOwn }).then((leaf) => {
      if (mounted) setIsLeaf(leaf);
    });
    return () => {
      mounted = false;
    };
  }, [currentPath, catalogue, isOwn, hasCatalogue]);

  const isAlreadySelected = value.some((item) => item.path === currentPath);
  const canAdd = isLeaf && !isAlreadySelected && !isResolving && !disabled;

  const handleAdd = async () => {
    if (!canAdd) return;
    setIsResolving(true);
    try {
      const resolved = await resolveActionLabel({
        catalogue,
        path: currentPath,
        isOwn,
        rootPath,
      });
      onChange?.([
        ...value,
        buildAction({ path: currentPath, label: resolved }),
      ]);
      setCurrentPath("");
      setIsLeaf(false);
      setSelectorKey((prev) => prev + 1);
    } finally {
      setIsResolving(false);
    }
  };

  const handleRemove = (path) => {
    onChange?.(value.filter((item) => item.path !== path));
  };

  // Modo plano: el Autocomplete es la fuente de la lista completa. Se conservan
  // los campos extra de las acciones que ya estaban (p.ej. la investigacion de
  // la que provienen), que el Autocomplete no conoce.
  const handleFlatChange = (_, selected) => {
    onChange?.(
      selected.map((option) => {
        const previous = value.find((item) => item.path === option.value);
        return {
          ...(previous || {}),
          ...buildAction({
            path: option.value,
            label: option.label,
            locales: option.locales || {},
          }),
        };
      })
    );
  };

  const handleAddAnother = () => onAnotherChange?.([...another, ""]);

  const handleAnotherChange = (idx, text) =>
    onAnotherChange?.(another.map((item, i) => (i === idx ? text : item)));

  const handleRemoveAnother = (idx) =>
    onAnotherChange?.(another.filter((_, i) => i !== idx));

  return (
    <Stack spacing={1.5}>
      {hasCatalogue ? (
        <Stack spacing={1}>
          <InputCatalogue
            key={`${catalogue}-${rootPath}-${selectorKey}`}
            label={label}
            catalogue={catalogue}
            isOwn={isOwn}
            rootPath={rootPath}
            onChange={setCurrentPath}
            isValid={showError ? false : undefined}
          />
          <Box display="flex" justifyContent="flex-end">
            <EGButton
              label={t("Resolution.add_to_list")}
              iconName="add_square"
              color={canAdd ? "primary" : "light"}
              iconColor={canAdd ? "white" : ""}
              iconColorHover={canAdd ? "white" : ""}
              labelColor={canAdd ? "white" : ""}
              disabled={!canAdd}
              loading={isResolving}
              onClick={handleAdd}
            />
          </Box>
        </Stack>
      ) : (
        <Autocomplete
          multiple
          disableCloseOnSelect
          disabled={disabled}
          options={options}
          getOptionLabel={(option) => option.label}
          value={options.filter((option) =>
            value.some((item) => item.path === option.value)
          )}
          onChange={handleFlatChange}
          isOptionEqualToValue={(option, selected) =>
            option.value === selected.value
          }
          renderTags={(tags, getTagProps) =>
            tags.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.value}
                label={option.label}
                sx={{
                  backgroundColor: theme.palette.blueGray100.main,
                  fontSize: 12,
                }}
              />
            ))
          }
          renderOption={(props, option, { selected }) => (
            <Box
              {...props}
              key={option.value}
              sx={{
                maxHeight: 47,
                p: 2,
                my: 0.5,
                mx: 1,
                borderRadius: 1.5,
                border: `${theme.palette.blueGray100.main} 1px solid`,
              }}
            >
              <Checkbox size="small" checked={selected} />
              <Typography variant="body2">{option.label}</Typography>
            </Box>
          )}
          renderInput={(params) => (
            <EGInput
              {...params}
              label={label}
              placeholder={placeholder}
              isValid={showError ? false : undefined}
            />
          )}
        />
      )}

      {/* En modo catalogo las elegidas se listan aparte; en modo plano ya salen
          como tags dentro del Autocomplete. */}
      {hasCatalogue && value.length > 0 && (
        <Box display="flex" flexWrap="wrap" gap={0.75}>
          {value.map((action) => (
            <Chip
              key={action.path}
              label={getActionLabel(action)}
              onDelete={disabled ? undefined : () => handleRemove(action.path)}
              sx={{
                backgroundColor: theme.palette.blueGray100.main,
                fontSize: 12,
              }}
            />
          ))}
        </Box>
      )}

      {onAnotherChange && (
        <Stack spacing={1}>
          {another.map((text, idx) => (
            <Stack
              key={`another-${idx}`}
              direction="row"
              spacing={0.5}
              alignItems="flex-start"
            >
              <EGInput
                label={`${freeTextLabel || t("Resolution.another")} #${
                  idx + 1
                }:`}
                placeholder={freeTextPlaceholder}
                fullWidth
                value={text}
                onChange={(e) => handleAnotherChange(idx, e.target.value)}
                required
                autoFocus
              />
              <EGIconButton
                iconName="close_icon"
                iconColor="error"
                onClick={() => handleRemoveAnother(idx)}
                title={t("Resolution.delete")}
              />
            </Stack>
          ))}
          <Box display="flex" justifyContent="flex-end">
            <EGIconButton
              iconName="add_square"
              iconColor="primary"
              title={addFreeTextTitle || t("Resolution.another")}
              onClick={handleAddAnother}
              disabled={disabled || another.some((text) => text.trim() === "")}
            />
          </Box>
        </Stack>
      )}
    </Stack>
  );
};
